import { Table } from "antd";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import EmployeeLeaveModelPopup from "../../../components/modelpopup/EmployeeLeaveModelPopup";
import { useLazyGetLeavesQuery, useSaveLeavesMutation, useCancelLeaveMutation } from "../../../services/hr.service";
import { useSelector, useDispatch } from "react-redux";
import { toggleDelete } from "../../../redux/slices/ui.slice";
import { LEAVES } from "../../../services/ENDPOINTS";
import dayjs from "dayjs";
import alertify from "alertifyjs";

const EmployeeLeave = () => {
  const [selected, setSelected] = useState(null);
  const [results, setResult] = useState([]);
  const [pagination, setPagination] = useState({});
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [getLeaves, { data, isFetching }] = useLazyGetLeavesQuery();
  const [saveLeave, { isLoading: isCreating }] = useSaveLeavesMutation();
  const [cancelLeave, { isLoading: isCancelling }] = useCancelLeaveMutation();

  useEffect(() => {
    if (user?.emp_id) {
      // Get leaves for the current employee
      getLeaves({
        filter: {
          employee_id: user.emp_id,
          // Get all statuses for employee view
        },
        page: 1
      });
    }
  }, [user?.emp_id]);

  useEffect(() => {
    if (data) {
      const leaves = Array.isArray(data?.data) ? data.data : [];
      setResult(leaves);
      setPagination(data?.meta || {});
    }
  }, [data]);

  const handleSubmit = async (leaveData) => {
    try {
      const result = await saveLeave({
        ...leaveData,
        employee_id: user?.emp_id,
      }).unwrap();

      if (result.success) {
        alertify.success(result.message || "Leave request submitted successfully");
        getLeaves({ filter: { employee_id: user.emp_id }, page: 1 });
        setSelected(null);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Failed to submit leave request");
    }
  };

  const handleCancel = async (leaveId) => {
    try {
      const result = await cancelLeave({
        id: leaveId,
        data: {
          cancelled_by: user?.emp_id,
          cancellation_reason: 'Cancelled by employee',
        }
      }).unwrap();

      if (result.success) {
        alertify.success(result.message || 'Leave request cancelled');
        getLeaves({ filter: { employee_id: user.id }, page: 1 });
      }
    } catch (error) {
      alertify.error(error?.data?.message || 'Failed to cancel leave');
    }
  };

  const doDelete = (e, item) => {
    dispatch(
      toggleDelete({
        open: true,
        title: 'Leave',
        url: `${LEAVES}/${item.id}`,
        ele: e.currentTarget,
        parent: 'tr'
      })
    );
  };

  // Calculate leave statistics from employee's leaves
  const stats = {
    total: results.length,
    pending: results.filter(l => l.status === 'Pending').length,
    approved: results.filter(l => l.status === 'Approved').length,
    rejected: results.filter(l => l.status === 'Rejected').length,
    totalDays: results
      .filter(l => l.status === 'Approved')
      .reduce((sum, leave) => sum + parseFloat(leave.number_of_days || 0), 0),
  };

  const leaveStats = [
    {
      id: 1,
      title: "Total Requests",
      value: stats.total,
    },
    {
      id: 2,
      title: "Pending",
      value: stats.pending,
    },
    {
      id: 3,
      title: "Approved",
      value: stats.approved,
    },
    {
      id: 4,
      title: "Total Days Taken",
      value: stats.totalDays,
    },
  ];

  const columns = [
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      render: (text, record) => <span>{record.leave_type?.name || "N/A"}</span>,
      sorter: (a, b) => (a.leave_type?.name || "").localeCompare(b.leave_type?.name || ""),
    },
    {
      title: "From",
      dataIndex: "start_date",
      render: (text) => <span>{dayjs(text).format('DD MMM YYYY')}</span>,
      sorter: (a, b) => new Date(a.start_date) - new Date(b.start_date),
    },
    {
      title: "To",
      dataIndex: "end_date",
      render: (text) => <span>{dayjs(text).format('DD MMM YYYY')}</span>,
      sorter: (a, b) => new Date(a.end_date) - new Date(b.end_date),
    },
    {
      title: "No Of Days",
      dataIndex: "number_of_days",
      render: (text) => <span>{text}</span>,
      sorter: (a, b) => a.number_of_days - b.number_of_days,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      render: (text) => (
        <span className="text-truncate" style={{ maxWidth: "200px", display: "inline-block" }}>
          {text || "N/A"}
        </span>
      ),
      sorter: (a, b) => (a.reason || "").localeCompare(b.reason || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <div className="dropdown action-label text-center">
          <Link
            className="btn btn-white btn-sm btn-rounded"
            to="#"
          >
            <i
              className={
                text === "New"
                  ? "far fa-dot-circle text-purple"
                  : text === "Pending"
                  ? "far fa-dot-circle text-info"
                  : text === "Approved"
                  ? "far fa-dot-circle text-success"
                  : text === "Rejected"
                  ? "far fa-dot-circle text-danger"
                  : "far fa-dot-circle text-secondary"
              }
            />{" "}
            {text}
          </Link>
        </div>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
    },
    {
      title: "Action",
      className: "text-end",
      render: (text, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            {record.status === 'Pending' && (
              <>
                <Link
                  className="dropdown-item"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#add_leave"
                  onClick={() => setSelected(record)}
                >
                  <i className="fa fa-pencil m-r-5" /> Edit
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={() => handleCancel(record.id)}
                >
                  <i className="fa fa-times m-r-5" /> Cancel
                </Link>
                <Link
                  className="dropdown-item"
                  to="#"
                  onClick={(e) => doDelete(e, record)}
                >
                  <i className="fa fa-trash m-r-5" /> Delete
                </Link>
              </>
            )}
            {record.status !== 'Pending' && (
              <Link className="dropdown-item disabled" to="#">
                <i className="fa fa-info-circle m-r-5" /> No actions available
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="My Leaves"
            title="Dashboard"
            subtitle="My Leaves"
            addButton={() => (
              <Link
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#add_leave"
                className="btn add-btn"
                onClick={() => setSelected(null)}
              >
                <i className="fa fa-plus" /> Apply for Leave
              </Link>
            )}
          />

          <div className="row">
            {leaveStats.map((stat, index) => (
              <div className="col-md-3" key={index}>
                <div className="stats-info">
                  <h6>{stat.title}</h6>
                  <h4>{stat.value}</h4>
                </div>
              </div>
            ))}
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="table-responsive">
                <Table
                  columns={columns}
                  dataSource={results}
                  rowKey={(record) => record.id}
                  loading={isFetching}
                  className="table-striped"
                  pagination={{
                    defaultPageSize: 10,
                    total: pagination?.total || results.length,
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                    onChange: (page, pageSize) => {
                      getLeaves({ filter: { employee_id: user.id }, page });
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Add Leave Modal */}
      <EmployeeLeaveModelPopup
        item={selected}
        onSubmit={handleSubmit}
        isLoading={isCreating}
      />
    </>
  );
};

export default EmployeeLeave;
